import { useQuery, type QueryClient } from '@tanstack/react-query'
import { discoveryApi } from '@/api/clients/discoveryApi'
import { playbookApi } from '@/api/clients/playbookApi'
import type * as discovery from '@/api/types/discovery'
import type * as playbook from '@/api/types/playbook'
import type { SyncRun } from '@/api/types/readiness'

// ── Sync state from qtz-readiness ───────────────────────────────────────────
// A sync is two pulls the backend runs and records: Discovery (Module 1) and
// Playbook (Module 3). Their summaries say whether each is connected (a pull
// has succeeded) and their sync-runs are the history the strip shows, so every
// browser sees the same state, including runs the backend scheduled itself.

// The query keys a sync makes stale: the backend's sync state and every read
// served from what the pulls wrote.
const SYNCED_READS = ['readiness-sync', 'readiness-heatmap', 'readiness-objective', 'discovery-dependencies', 'playbook-plans'] as const

export function invalidateSyncedReads(qc: QueryClient, orgId: string) {
  return Promise.all(SYNCED_READS.map((key) => qc.invalidateQueries({ queryKey: [key, orgId] })))
}

// A backend run in the strip's SyncRun shape. A partial run wrote what it
// could, so it counts as succeeded with its errors kept.
function toSyncRun(r: discovery.SyncRun | playbook.SyncRun, module: 'discovery' | 'playbook', orgId: string, indexId: string): SyncRun {
  return {
    id: `${module}-${r.id}`,
    org_id: orgId,
    index_id: indexId,
    started_at: r.started_at,
    finished_at: r.finished_at,
    status: r.status === 'partial' ? 'succeeded' : r.status,
    error: r.errors?.length ? `${module === 'discovery' ? 'Discovery' : 'Playbook'}: ${r.errors.join('; ')}` : null,
    discovery_connected: module === 'discovery' && r.status !== 'failed',
    playbook_connected: module === 'playbook' && r.status !== 'failed',
    evidence_created: 0,
    suggestions_written: 0,
    trigger: r.trigger,
  }
}

const byStartDesc = (a: SyncRun, b: SyncRun) => b.started_at.localeCompare(a.started_at)

export interface BackendSyncState {
  runs: SyncRun[] // both modules' runs, newest first
  discoveryConnected: boolean
  playbookConnected: boolean
}

// Poll while a pull the backend started in the background is still running.
const RUNNING_POLL_MS = 3_000

export function useBackendSync(orgId: string, indexId: string) {
  const enabled = !!orgId
  const q = useQuery({
    queryKey: ['readiness-sync', orgId],
    queryFn: async (): Promise<BackendSyncState> => {
      const [dSum, dRuns, pSum, pRuns] = await Promise.allSettled([discoveryApi.summary(orgId), discoveryApi.syncRuns(orgId), playbookApi.summary(orgId), playbookApi.syncRuns(orgId)])
      const runs = [
        ...(dRuns.status === 'fulfilled' ? dRuns.value : []).map((r) => toSyncRun(r, 'discovery', orgId, indexId)),
        ...(pRuns.status === 'fulfilled' ? pRuns.value : []).map((r) => toSyncRun(r, 'playbook', orgId, indexId)),
      ].sort(byStartDesc)
      // A summary that does not answer is a module that is not connected.
      return {
        runs,
        discoveryConnected: dSum.status === 'fulfilled' && dSum.value?.connected === true,
        playbookConnected: pSum.status === 'fulfilled' && pSum.value?.connected === true,
      }
    },
    enabled,
    retry: false,
    refetchInterval: (query) => (query.state.data?.runs.some((r) => r.status === 'running') ? RUNNING_POLL_MS : false),
  })
  return { enabled, loaded: q.isSuccess, data: q.data ?? null }
}
